import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Download, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { SignInRequired } from "@/components/SignInRequired";
import {
  fetchPayoutStatement,
  downloadStatementCsv,
  downloadStatementPdf,
  type PayoutStatement,
} from "@/lib/affiliate-statement";

export const Route = createFileRoute("/affiliate/statement/$id")({
  component: StatementPage,
  head: () => ({
    meta: [
      { title: "Payout statement - Inquira Creator Program" },
      { name: "description", content: "Download your Inquira affiliate payout statement with totals, fees, and transaction IDs." },
      { property: "og:title", content: "Inquira payout statement" },
      { property: "og:description", content: "Monthly affiliate payout statement with commission breakdown." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

const money = (c: number) => `$${((c ?? 0) / 100).toFixed(2)}`;

function StatementPage() {
  const { id } = Route.useParams();
  const { user, isLoading: authLoading } = useAuth();
  const [statement, setStatement] = useState<PayoutStatement | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    let active = true;
    setLoading(true);
    void fetchPayoutStatement(id).then((res) => {
      if (!active) return;
      setStatement(res);
      setLoading(false);
      if (!res.ok) toast.error(res.error === "not_found" ? "Statement not found" : (res.error ?? "Failed to load"));
    });
    return () => {
      active = false;
    };
  }, [id, user]);

  if (!authLoading && !user) return <SignInRequired title="Sign in to view your payout statement" />;

  const p = statement?.payout;

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <div className="mb-4 flex items-center justify-between gap-3 print:hidden">
        <Link to="/affiliate" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </Link>
        {statement?.ok && p && (
          <div className="flex gap-2">
            <button
              onClick={() => downloadStatementCsv(statement)}
              className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-2 text-xs font-medium"
            >
              <Download className="h-3.5 w-3.5" /> CSV
            </button>
            <button
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await downloadStatementPdf(statement);
                } catch {
                  toast.error("Could not generate the PDF");
                } finally {
                  setBusy(false);
                }
              }}
              className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} PDF
            </button>
          </div>
        )}
      </div>

      {loading || authLoading ? (
        <div className="rounded-2xl border border-border p-8 text-center text-sm text-muted-foreground">Loading statement…</div>
      ) : !statement?.ok || !p ? (
        <div className="rounded-2xl border border-border p-8 text-center text-sm text-muted-foreground">
          This statement isn’t available. Statements appear once a payout batch has been created for your account.
        </div>
      ) : (
        <div className="space-y-4">
          <header className="rounded-2xl border border-border p-5">
            <h1 className="text-xl font-bold">Payout statement</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {p.batch_key ? `Batch ${p.batch_key} · ` : ""}
              {p.period_start ? new Date(p.period_start).toLocaleDateString() : "—"} –{" "}
              {p.period_end ? new Date(p.period_end).toLocaleDateString() : "—"}
            </p>
            <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-muted-foreground">Creator</dt>
                <dd className="font-medium">{statement.affiliate?.creator_name ?? statement.affiliate?.affiliate_code ?? "—"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Legal name</dt>
                <dd className="font-medium">{statement.affiliate?.legal_name ?? "Not submitted"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Status</dt>
                <dd className="font-medium capitalize">{p.status}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Paid at</dt>
                <dd className="font-medium">{p.paid_at ? new Date(p.paid_at).toLocaleString() : "Not yet paid"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Method</dt>
                <dd className="font-medium capitalize">{p.method ?? statement.affiliate?.payout_method ?? "paypal"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Transaction ID</dt>
                <dd className="font-medium break-all">{p.transaction_reference ?? "Pending"}</dd>
              </div>
            </dl>
          </header>

          <section className="rounded-2xl border border-border p-5">
            <h2 className="mb-3 text-lg font-semibold">Totals</h2>
            <ul className="space-y-2 text-sm">
              {[
                ["Commissions included", String(statement.summary?.commission_count ?? 0)],
                ["Referred revenue", money(statement.summary?.gross_revenue_cents ?? 0)],
                ["Gross commission", money(p.gross_cents)],
                ["Processing fee", `-${money(p.fee_cents)}`],
              ].map(([k, v]) => (
                <li key={k} className="flex justify-between gap-3">
                  <span className="text-muted-foreground">{k}</span>
                  <span className="font-medium">{v}</span>
                </li>
              ))}
              <li className="flex justify-between gap-3 border-t border-border pt-2 text-base">
                <span className="font-semibold">Net paid</span>
                <span className="font-bold text-neon-pink">{money(p.net_cents)}</span>
              </li>
            </ul>
          </section>

          <section className="rounded-2xl border border-border p-5">
            <h2 className="mb-3 text-lg font-semibold">Commission breakdown</h2>
            {(statement.items?.length ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground">No individual commissions recorded for this batch.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="py-2 pr-3">Date</th>
                      <th className="py-2 pr-3">Source</th>
                      <th className="py-2 pr-3">Transaction ID</th>
                      <th className="py-2 pr-3 text-right">Revenue</th>
                      <th className="py-2 text-right">Commission</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {statement.items!.map((it, i) => (
                      <tr key={`${it.invoice_id ?? "item"}-${i}`}>
                        <td className="py-2 pr-3">{new Date(it.created_at).toLocaleDateString()}</td>
                        <td className="py-2 pr-3 capitalize">{it.billing_platform}</td>
                        <td className="py-2 pr-3 text-xs text-muted-foreground break-all">{it.invoice_id ?? "—"}</td>
                        <td className="py-2 pr-3 text-right">${Number(it.eligible_net_amount ?? 0).toFixed(2)}</td>
                        <td className="py-2 text-right font-medium">{money(it.amount_cents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="mt-4 text-xs text-muted-foreground">
              Payouts are issued monthly once your available balance reaches $25. Anything below that carries forward.
            </p>
          </section>
        </div>
      )}
    </div>
  );
}
